import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as adminApi from '../services/adminApi';

const ConfigurationContext = createContext();

export const useConfiguration = () => {
  const context = useContext(ConfigurationContext);
  if (!context) {
    throw new Error('useConfiguration must be used within a ConfigurationProvider');
  }
  return context;
};

export const ConfigurationProvider = ({ children }) => {
  const [cityDataAvailable, setCityDataAvailable] = useState({});
  const [indicatorsWithData, setIndicatorsWithData] = useState({});
  const [cityApiKeys, setCityApiKeys] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load configuration on mount
  useEffect(() => {
    loadConfiguration();
  }, []);

  // Load complete configuration
  const loadConfiguration = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await adminApi.getCompleteConfiguration();
      if (result.success) {
        setCityDataAvailable(result.data.cityDataAvailable || {});
        setIndicatorsWithData(result.data.indicatorsWithData || {});
        setCityApiKeys(result.data.cityApiKeys || {});
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading configuration:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check if city has data available
  const isCityAvailable = useCallback((city) => {
    return cityDataAvailable[city] === true;
  }, [cityDataAvailable]);

  // Check if indicator has data for a city
  const hasIndicatorData = useCallback((city, indicatorName) => {
    if (!indicatorsWithData[city]) return false;
    return indicatorsWithData[city].includes(indicatorName);
  }, [indicatorsWithData]);

  // Check if city has any indicator data (so ticker can match what's actually shown)
  const hasAnyIndicatorData = useCallback((city) => {
    const list = indicatorsWithData[city];
    return Array.isArray(list) && list.length > 0;
  }, [indicatorsWithData]);

  // Get API key for a city
  const getCityApiKey = useCallback((city) => {
    return cityApiKeys[city] || `DASHBOARD_${city.toUpperCase().replace(/\s+/g, '_')}_KEY`;
  }, [cityApiKeys]);

  // Update city availability
  const updateCityAvailability = useCallback(async (city, isAvailable) => {
    try {
      const result = await adminApi.updateCityAvailability(city, isAvailable);
      if (result.success) {
        setCityDataAvailable(result.data);
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Update city API key
  const updateCityApiKey = useCallback(async (city, apiKey) => {
    try {
      const result = await adminApi.updateCityApiKey(city, apiKey);
      if (result.success) {
        setCityApiKeys(result.data);
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Add indicator to city
  const addIndicatorToCity = useCallback(async (city, indicatorName) => {
    try {
      const result = await adminApi.addIndicatorToCity(city, indicatorName);
      if (result.success) {
        setIndicatorsWithData(prev => ({
          ...prev,
          [city]: result.data
        }));
        // Dispatch event to notify components of configuration change
        window.dispatchEvent(new CustomEvent('configurationChanged', { detail: { city, action: 'add', indicatorName } }));
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Remove indicator from city
  const removeIndicatorFromCity = useCallback(async (city, indicatorName) => {
    try {
      const result = await adminApi.removeIndicatorFromCity(city, indicatorName);
      if (result.success) {
        setIndicatorsWithData(prev => ({
          ...prev,
          [city]: result.data
        }));
        // Dispatch event to notify components of configuration change
        window.dispatchEvent(new CustomEvent('configurationChanged', { detail: { city, action: 'remove', indicatorName } }));
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Update city indicators
  const updateCityIndicators = useCallback(async (city, indicators) => {
    try {
      const result = await adminApi.updateCityIndicators(city, indicators);
      if (result.success) {
        setIndicatorsWithData(prev => ({
          ...prev,
          [city]: result.data
        }));
        // Dispatch event to notify components of configuration change
        window.dispatchEvent(new CustomEvent('configurationChanged', { detail: { city, indicators } }));
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Reset configuration
  const resetConfiguration = useCallback(async () => {
    try {
      const result = await adminApi.resetConfiguration();
      if (result.success) {
        setCityDataAvailable(result.data.cityDataAvailable);
        setIndicatorsWithData(result.data.indicatorsWithData);
        setCityApiKeys(result.data.cityApiKeys);
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Export configuration
  const exportConfiguration = useCallback(async () => {
    try {
      const result = await adminApi.exportConfiguration();
      return result;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Import configuration
  const importConfiguration = useCallback(async (configData) => {
    try {
      const result = await adminApi.importConfiguration(configData);
      if (result.success) {
        await loadConfiguration(); // Reload after import
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [loadConfiguration]);

  const value = {
    // State
    cityDataAvailable,
    indicatorsWithData,
    cityApiKeys,
    isLoading,
    error,
    
    // Methods
    loadConfiguration,
    isCityAvailable,
    hasIndicatorData,
    hasAnyIndicatorData,
    getCityApiKey,
    updateCityAvailability,
    updateCityApiKey,
    addIndicatorToCity,
    removeIndicatorFromCity,
    updateCityIndicators,
    resetConfiguration,
    exportConfiguration,
    importConfiguration
  };

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
};
